import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Gavel, FileText, Scale } from 'lucide-react';
import { GoogleGenAI, LiveServerMessage, Modality, Blob as GenAIBlob, Type } from '@google/genai';
import './LawyerPage.scss';
import '../../App.scss';

interface Note {
  id: number;
  content: string;
  timestamp: string;
}

interface CaseSummary {
  caseType: string;
  ipcSections: string;
  nyayaSections: string;
  legalAngles: string;
}

interface LegalSummary {
  classification: string;
  ipc: string;
  bns: string;
}

// Helper Functions for Audio Processing
function decode(base64: string): Uint8Array {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function encode(bytes: Uint8Array): string {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}

function createBlob(data: Float32Array): GenAIBlob {
  const l = data.length;
  const int16 = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    int16[i] = data[i] * 32768;
  }
  return {
    data: encode(new Uint8Array(int16.buffer)),
    mimeType: 'audio/pcm;rate=16000',
  };
}

function parseMarkdown(text: string): string {
  const applyInlineStyles = (line: string) => {
    return line
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>');
  };

  const lines = text.split('\n');
  let html = '';
  let inParagraph = false;

  const closeParagraph = () => {
    if (inParagraph) {
      html += '</p>';
      inParagraph = false;
    }
  };

  for (const line of lines) {
    if (line.startsWith('#### ')) {
      closeParagraph();
      html += `<h4>${applyInlineStyles(line.substring(5))}</h4>`;
      continue;
    }
    if (line.startsWith('### ')) {
      closeParagraph();
      html += `<h3>${applyInlineStyles(line.substring(4))}</h3>`;
      continue;
    }
    if (line.startsWith('## ')) {
      closeParagraph();
      html += `<h2>${applyInlineStyles(line.substring(3))}</h2>`;
      continue;
    }
    if (line.startsWith('# ')) {
      closeParagraph();
      html += `<h1>${applyInlineStyles(line.substring(2))}</h1>`;
      continue;
    }
    if (line.match(/^(---|___|\*\*\*)\s*$/)) {
      closeParagraph();
      html += '<hr />';
      continue;
    }
    if (line.trim() === '') {
      closeParagraph();
      continue;
    }
    if (!inParagraph) {
      html += '<p>';
      inParagraph = true;
    } else {
      html += '<br />';
    }
    html += applyInlineStyles(line);
  }
  closeParagraph();
  return html;
}

const LawyerPage = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [caseSummary, setCaseSummary] = useState<CaseSummary>({
    caseType: "",
    ipcSections: "",
    nyayaSections: "",
    legalAngles: "",
  });
  const [caseNotes, setCaseNotes] = useState<string>('');
  const [legalSummary, setLegalSummary] = useState<LegalSummary | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sessionPromiseRef = useRef<Promise<any> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const outputAudioContextRef = useRef<AudioContext | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const mediaStreamSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const currentInputTranscriptionRef = useRef<string>('');
  const currentOutputTranscriptionRef = useRef<string>('');
  const outputAudioSources = useRef<Set<AudioBufferSourceNode>>(new Set());
  const nextStartTime = useRef(0);

  const API_KEY = process.env.REACT_APP_GEMINI_API_KEY as string;
  if (typeof API_KEY !== "string") {
    throw new Error("set REACT_APP_GEMINI_API_KEY in .env");
  }

  const addNotes = useCallback((note: string) => {
    setNotes(prevNotes => {
      const newNote = {
        id: prevNotes.length + 1,
        content: note,
        timestamp: new Date().toLocaleTimeString()
      };
      return [...prevNotes, newNote];
    });
  }, []);

  const addSummary = useCallback((summary: CaseSummary) => {
    setCaseSummary(summary);
  }, []);

  const generateLegalSummary = useCallback(async (notes: string) => {
    if (!notes) return;
    setLegalSummary(null);
    try {
      const ai = new GoogleGenAI({ apiKey: API_KEY });
      const prompt = `Analyze the following legal case notes and provide a structured summary.

Case Notes:

---

${notes}

---

Provide your analysis in a JSON format with three keys:

1. "classification": A brief classification of the case (e.g., "Civil - Property Dispute", "Criminal - Theft").
2. "ipc": List potentially relevant sections of the Indian Penal Code (IPC). If none, state "Not Applicable".
3. "bns": List potentially relevant sections of the Bharatiya Nyaya Sanhita (BNS). If none, state "Not Applicable".`;

      const response = await ai.models.generateContent({
        model: 'gemini-2.5-pro',
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              classification: { type: Type.STRING },
              ipc: { type: Type.STRING },
              bns: { type: Type.STRING },
            },
          },
        },
      });
      const summaryText = response.text?.trim();
      if (summaryText) {
        const summaryData = JSON.parse(summaryText) as LegalSummary;
        setLegalSummary(summaryData);
      }
    } catch (err) {
      console.error('Legal summary generation failed:', err);
      setError('Failed to generate legal analysis.');
    }
  }, [API_KEY]);

  const handleSummarize = useCallback(async (fullTranscript: string) => {
    if (!fullTranscript) return;
    setIsLoading(true);
    setError(null);
    setCaseNotes('');
    setLegalSummary(null);

    try {
      const ai = new GoogleGenAI({ apiKey: API_KEY });
      const prompt = `Based on the following conversation between a Client and an AI Lawyer, summarize the key details of the case into well-structured notes. Use markdown for clear formatting.

Conversation:

---

${fullTranscript}

---

Generate comprehensive notes covering the main points, timeline, people involved, and any critical details mentioned.`;

      const response = await ai.models.generateContent({
        model: 'gemini-2.5-pro',
        contents: prompt,
      });
      const notesText = response.text || '';
      if (notesText) {
        setCaseNotes(notesText);
        await generateLegalSummary(notesText);
      }
    } catch (err) {
      console.error('Summarization failed:', err);
      setError('Failed to generate case notes.');
    } finally {
      setIsLoading(false);
    }
  }, [API_KEY, generateLegalSummary]);

  const cleanup = useCallback(() => {
    sessionPromiseRef.current?.then(session => session.close()).catch(() => {});
    sessionPromiseRef.current = null;
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    outputAudioSources.current.forEach(source => source.stop());
    outputAudioSources.current.clear();
    nextStartTime.current = 0;
    scriptProcessorRef.current?.disconnect();
    scriptProcessorRef.current = null;
    mediaStreamSourceRef.current?.disconnect();
    mediaStreamSourceRef.current = null;
    inputAudioContextRef.current?.close().catch(() => {});
    inputAudioContextRef.current = null;
    outputAudioContextRef.current?.close().catch(() => {});
    outputAudioContextRef.current = null;
    setConnected(false);
  }, []);

  const handleDisconnect = useCallback(() => {
    const fullTranscriptText = (currentInputTranscriptionRef.current || '') + ' ' + (currentOutputTranscriptionRef.current || '');
    cleanup();
    if (fullTranscriptText.trim()) {
      handleSummarize(fullTranscriptText.trim());
    }
  }, [cleanup, handleSummarize]);

  const handleConnect = useCallback(async () => {
    setConnected(false);
    setError(null);
    currentInputTranscriptionRef.current = '';
    currentOutputTranscriptionRef.current = '';

    try {
      const ai = new GoogleGenAI({ apiKey: API_KEY });
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      inputAudioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
      outputAudioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });

      sessionPromiseRef.current = ai.live.connect({
        model: 'gemini-2.5-flash-native-audio-preview-09-2025',
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } } },
          systemInstruction: 'You are an Indian lawyer. Talk in English. Listen to clients and take notes of all the conversation. You answer questions and provide legal advice based on the conversation. Add all the details the notes and at the end make a summary with possible legal angles. Add Bharatiya samhita section, IPC section in summary. You help senior lawyer by collecting all the information. You name is Bhashini from firm Vani & Vadi associates.',
          inputAudioTranscription: {},
          outputAudioTranscription: {},
        },
        callbacks: {
          onopen: () => {
            setConnected(true);
            if (streamRef.current && inputAudioContextRef.current) {
              mediaStreamSourceRef.current = inputAudioContextRef.current.createMediaStreamSource(streamRef.current);
              scriptProcessorRef.current = inputAudioContextRef.current.createScriptProcessor(4096, 1, 1);
              scriptProcessorRef.current.onaudioprocess = (audioProcessingEvent) => {
                const inputData = audioProcessingEvent.inputBuffer.getChannelData(0);
                sessionPromiseRef.current?.then((session) => session.sendRealtimeInput({ media: createBlob(inputData) }));
              };
              mediaStreamSourceRef.current.connect(scriptProcessorRef.current);
              scriptProcessorRef.current.connect(inputAudioContextRef.current.destination);
            }
          },
          onmessage: async (message: LiveServerMessage) => {
            if (message.serverContent?.inputTranscription) {
              currentInputTranscriptionRef.current += message.serverContent.inputTranscription.text;
            }
            if (message.serverContent?.outputTranscription) {
              currentOutputTranscriptionRef.current += message.serverContent.outputTranscription.text;
            }
                         if (message.serverContent?.turnComplete) {
               // Clear transcription buffers but don't add them as notes
               // Notes should only come from addNotes tool calls
               currentInputTranscriptionRef.current = '';
               currentOutputTranscriptionRef.current = '';
             }

                         // Handle tool calls
             if (message.toolCall?.functionCalls) {
               const functionResponses: any[] = [];
               
               for (const functionCall of message.toolCall.functionCalls) {
                 switch (functionCall.name) {
                   case "addNotes":
                     const text = functionCall.args?.text;
                     if (typeof text === 'string' && text) {
                       addNotes(text);
                       functionResponses.push({
                         name: functionCall.name,
                         response: { success: true },
                       });
                     }
                     break;
                   case "createSummary":
                     const args = functionCall.args as any;
                     if (args && typeof args === 'object') {
                       addSummary({
                         caseType: (typeof args?.caseType === 'string') ? args.caseType : "",
                         ipcSections: (typeof args?.ipcSections === 'string') ? args.ipcSections : "",
                         nyayaSections: (typeof args?.nyayaSections === 'string') ? args.nyayaSections : "",
                         legalAngles: (typeof args?.legalAngles === 'string') ? args.legalAngles : "",
                       });
                       functionResponses.push({
                         name: functionCall.name,
                         response: { success: true },
                       });
                     }
                     break;
                 }
               }
               
               // Send tool responses back to the AI
               if (functionResponses.length > 0) {
                 sessionPromiseRef.current?.then((session) => {
                   session.sendToolResponse({ functionResponses });
                 });
               }
             }

            const base64Audio = message.serverContent?.modelTurn?.parts?.[0]?.inlineData?.data;
            if (base64Audio) {
              const audioContext = outputAudioContextRef.current!;
              nextStartTime.current = Math.max(nextStartTime.current, audioContext.currentTime);
              const audioBuffer = await decodeAudioData(decode(base64Audio), audioContext, 24000, 1);
              const source = audioContext.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(audioContext.destination);
              source.addEventListener('ended', () => outputAudioSources.current.delete(source));
              source.start(nextStartTime.current);
              nextStartTime.current += audioBuffer.duration;
              outputAudioSources.current.add(source);
            }

            if (message.serverContent?.interrupted) {
              outputAudioSources.current.forEach(source => source.stop());
              outputAudioSources.current.clear();
              nextStartTime.current = 0;
            }
          },
          onerror: (e: ErrorEvent) => {
            console.error('Session error:', e);
            setError('An error occurred with the connection.');
            cleanup();
          },
          onclose: handleDisconnect,
        },
      });
    } catch (err) {
      console.error('Connection failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect. Please check permissions and API key.');
      cleanup();
    }
  }, [API_KEY, cleanup, handleDisconnect, addNotes, addSummary]);

  useEffect(() => () => cleanup(), [cleanup]);

  const renderNotes = () => {
    if (isLoading) return <div className="lawyer-empty-state">Generating notes & analysis...</div>;
    if (caseNotes) return <div className="lawyer-notes-container" dangerouslySetInnerHTML={{ __html: parseMarkdown(caseNotes) }} />;
    if (notes.length === 0) {
      return <div className="lawyer-empty-state"><p>No case notes recorded yet. Begin your consultation to generate notes.</p></div>;
    }
    // Display notes from tool calls (not transcriptions)
    return (
      <div className="lawyer-notes-list">
        {notes.map((note) => (
          <div key={note.id} className="lawyer-note-item">
            <div className="lawyer-note-content">{note.content}</div>
            <div className="lawyer-note-timestamp">{note.timestamp}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="lawyer-page-container">
      <header className="lawyer-header">
        <div className="lawyer-header__content">
          <div className="lawyer-logo">
            <Gavel className="gavel-icon" />
            <div className="lawyer-branding">
              <h1 className="lawyer-firm-name">VANI &amp; VADI ASSOCIATES</h1>
              <p className="lawyer-firm-tagline">Legal Counsel &amp; Advisory Services</p>
            </div>
          </div>
          <div className="lawyer-header__decorative">
            <div className="decorative-line"></div>
          </div>
        </div>
      </header>

      <main className="lawyer-main-content">
        <div className="lawyer-content-wrapper">
          <section className="lawyer-case-notes">
            <div className="lawyer-section-header">
              <FileText className="section-icon" />
              <h2>Case Notes &amp; Proceedings</h2>
            </div>
            <div className="lawyer-notes-container">
              {renderNotes()}
            </div>
          </section>

          <section className="lawyer-case-summary">
            <div className="lawyer-section-header">
              <Scale className="section-icon" />
              <h2>Legal Case Summary</h2>
            </div>
            <div className="lawyer-summary-content">
              <div className="lawyer-summary-section">
                <h3>CASE CLASSIFICATION</h3>
                <p>{legalSummary?.classification || caseSummary.caseType || (isLoading ? 'Analyzing...' : 'Pending analysis...')}</p>
              </div>
              <div className="lawyer-summary-section">
                <h3>INDIAN PENAL CODE (IPC) SECTIONS</h3>
                <p>{legalSummary?.ipc || caseSummary.ipcSections || (isLoading ? 'Analyzing...' : 'Awaiting legal review...')}</p>
              </div>
              <div className="lawyer-summary-section">
                <h3>BHARATIYA NYAYA SANHITA SECTIONS</h3>
                <p>{legalSummary?.bns || caseSummary.nyayaSections || (isLoading ? 'Analyzing...' : 'Awaiting legal review...')}</p>
              </div>
              <div className="lawyer-summary-section">
                <h3>LEGAL ANGLES</h3>
                <p>{caseSummary.legalAngles || (isLoading ? 'Analyzing...' : 'Awaiting legal review...')}</p>
              </div>
            </div>
          </section>
        </div>
      </main>

      {error && (
        <div className="lawyer-error" role="alert">
          <strong>Error:</strong> <span>{error}</span>
        </div>
      )}

      <div className="lawyer-voice-control">
        <button
          className={`lawyer-connect-button ${connected ? 'connected' : ''}`}
          onClick={connected ? handleDisconnect : handleConnect}
          disabled={isLoading}
          aria-label={connected ? "Disconnect" : "Connect"}
        >
          {connected ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect width="18" height="18" x="3" y="3" rx="2"/>
              <line x1="12" y1="3" x2="12" y2="21"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
          )}
        </button>
        <p className="lawyer-voice-status">
          {isLoading ? 'Processing...' : (connected ? 'Consultation in Progress...' : 'Ready to Connect')}
        </p>
      </div>
    </div>
  );
};

export default LawyerPage;
